import { useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * SupportWidget — floating chat widget on the landing page.
 * Opens a compose card, posts to /api/support/message, shows confirmation.
 * Does NOT need auth — anonymous users can also send.
 *
 * Hidden on /app/profile because that surface has its own help affordances
 * (Referral card, Business tools menu) and the FAB was overlapping them on
 * mobile (Iter25 code-review nit).
 */
export default function SupportWidget({ prefillEmail = "" }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    from_email: prefillEmail,
    from_name: "",
    message: "",
  });
  const location = useLocation();
  if (location.pathname.startsWith("/app/profile")) return null;

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!form.from_email.includes("@")) return setError("Enter a valid email so we can reply.");
    if (form.message.trim().length < 5) return setError("Tell us a bit more so we can help.");
    setSending(true);
    try {
      await axios.post(`${BASE}/api/support/message`, {
        ...form,
        page: typeof window !== "undefined" ? window.location.pathname : null,
      });
      setSent(true);
      // Meta Pixel: submitted support message = Lead
      try {
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          window.fbq("track", "Lead", { content_name: "Support widget", content_category: form.subject || "General" });
          window.fbq("track", "Contact");
        }
      } catch { /* noop */ }
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send. Try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-24 sm:bottom-5 right-5 z-40 bg-kudi-green text-white w-14 h-14 rounded-full shadow-xl shadow-kudi-green/30 flex items-center justify-center hover:bg-kudi-green-dark hover:scale-105 active:scale-95 transition-transform"
        data-testid="floating-support-btn"
        aria-label="Contact support"
        title={open ? "Close support chat" : "Need help? Contact support"}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          className="fixed bottom-44 sm:bottom-24 right-5 z-40 w-[92vw] sm:w-96 bg-white rounded-2xl shadow-2xl border border-border overflow-hidden animate-in slide-in-from-bottom-2 duration-200"
          data-testid="support-widget"
        >
          <div className="bg-kudi-green text-white px-4 py-3 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            <div>
              <div className="font-heading font-semibold">Kobolog support</div>
              <div className="text-[11px] opacity-85">We usually reply within a few hours</div>
            </div>
          </div>

          {sent ? (
            <div className="p-6 text-center" data-testid="support-sent">
              <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3">✓</div>
              <div className="font-heading font-semibold">Message received</div>
              <div className="text-sm text-kudi-ink/60 mt-1">We&apos;ll reply to <span className="font-medium">{form.from_email}</span>.</div>
              <button
                onClick={() => { setSent(false); setOpen(false); setForm({ from_email: prefillEmail, from_name: "", message: "" }); }}
                className="mt-4 text-sm text-kudi-green hover:underline"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-4 space-y-2.5" data-testid="support-form">
              <input
                type="email"
                required
                value={form.from_email}
                onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                placeholder="Your email *"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:border-kudi-green focus:outline-none"
                data-testid="support-email"
              />
              <input
                type="text"
                value={form.from_name}
                onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                placeholder="Your name (optional)"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:border-kudi-green focus:outline-none"
                data-testid="support-name"
              />
              <textarea
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How can we help? *"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:border-kudi-green focus:outline-none resize-none"
                data-testid="support-message"
                maxLength={4000}
              />
              {error && <div className="text-xs text-rose-600" data-testid="support-error">{error}</div>}
              <button
                type="submit"
                disabled={sending}
                className="w-full bg-kudi-green text-white rounded-lg py-2.5 font-semibold flex items-center justify-center gap-1.5 hover:bg-kudi-green-dark transition disabled:opacity-50"
                data-testid="support-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Sending…" : "Send message"}
              </button>
              <div className="text-[10px] text-kudi-ink/50 text-center">
                Or WhatsApp us on{" "}
                <a
                  href="https://wa.me/2347010599993?text=Hi%20Kobolog%20support%2C%20I%20need%20help%20with..."
                  target="_blank" rel="noopener noreferrer"
                  className="text-kudi-green hover:underline font-medium"
                  data-testid="support-whatsapp-link"
                >
                  +234 701 059 9993
                </a>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
}
